import type { MarketDataDto } from "../../../../../shared/types/MarketDataDto";
import { PortfolioSettingsModel } from "../../../../portfolio-info/types/PortfolioSettingsModel";
import { PortfolioProfile } from "../../../../../shared/types/PortfolioProfile";
import { mathCalculationService } from "../../../../../shared/services/MathCalculationService";
import { PortfolioRecord } from "../../../../../shared/types/PortfolioRecord";
import { BaseChartStyle } from "../styles/BaseChartStyle";
import { colourChartStyle } from "../styles/ColourChartStyle";
import { NumberFormatOptions, numberFormatters } from "../../../../../screens/shared/utils/numberFormatters";
import {
    PortfolioPerformanceContext,
    portfolioPerformanceService
} from "../../../../../shared/services/PortfolioPerformanceService";

export abstract class BaseChartDataSeriesService {
    protected style: BaseChartStyle;

    constructor() {
        this.style = colourChartStyle;
    }
    
    setStyle = (style: BaseChartStyle): void => {
        this.style = style;
    }
    
    protected getSeries = (name: string, lineColour: string, lineType: string, lineWidth: number, valuePrecision: number) => {
        return {
            animation: false,
            name: name,
            type: 'line',
            smooth: false,
            color: lineColour,
            seriesLayoutBy: 'row',
            emphasis: { focus: 'series' },
            lineStyle: {
                type: lineType,
                width: lineWidth,
            },
            showSymbol: false,
            yAxisIndex: 0,
            tooltip: {
                valueFormatter: (value: number) => value?.toFixed(valuePrecision) ?? 'Not available'
            },
        };
    }

    protected getMarkAreas = (portfolioSettings: PortfolioSettingsModel) => {
        return {
            silent: true,
            data: [
                [
                    {
                        xAxis: 'min',
                        itemStyle: {
                            color: this.style.outTrendAreaColour
                        }
                    },
                    {
                        xAxis: portfolioSettings.showSupportLine
                            ? (portfolioSettings.supportLinePrice - portfolioSettings.minUnderlyingPrice) / portfolioSettings.calculationStep
                            : -1,
                        itemStyle: {
                            color: this.style.outTrendAreaColour
                        }
                    }
                ],
                [
                    {
                        xAxis: portfolioSettings.showResistanceLine
                            ? (portfolioSettings.resistanceLinePrice - portfolioSettings.minUnderlyingPrice) / portfolioSettings.calculationStep
                            : -1,
                        itemStyle: {
                            color: this.style.outTrendAreaColour
                        }
                    },
                    {
                        xAxis: 'max',
                        itemStyle: {
                            color: this.style.outTrendAreaColour
                        }
                    }
                ]
            ],
        };
    }
    
    protected getMarkLines = (
        marketDataDto: MarketDataDto,
        portfolioProfile: PortfolioProfile,
        comparedPortfolioProfile: PortfolioProfile,
        portfolioSettings: PortfolioSettingsModel,
        performanceContext?: PortfolioPerformanceContext,
    ) => {
        const deltaNeutralPriceForCurrentPortfolio = this.getDeltaNeutralPrice(portfolioProfile);
        const deltaNeutralPriceForComparedPortfolio =
            portfolioSettings.useComparedPortfolio
                ? this.getDeltaNeutralPrice(comparedPortfolioProfile)
                : null;
        
        const data: any[] = [];

        data.push(this.getZeroBalanceLine());
        data.push(this.getUnderlyingPriceLine(
            portfolioProfile,
            marketDataDto,
            portfolioSettings,
            deltaNeutralPriceForCurrentPortfolio,
            performanceContext,
        ));
        
        if (portfolioSettings.showSupportLine) {
            data.push(this.getPriceLine(marketDataDto?.UnderlyingPrice!, portfolioSettings.supportLinePrice,
                portfolioSettings.showSupportLine, portfolioProfile, portfolioSettings, performanceContext));
        }

        if (portfolioSettings.showResistanceLine) {
            data.push(this.getPriceLine(marketDataDto?.UnderlyingPrice!, portfolioSettings.resistanceLinePrice,
                portfolioSettings.showResistanceLine, portfolioProfile, portfolioSettings, performanceContext));
        }

        if (deltaNeutralPriceForCurrentPortfolio !== null && portfolioSettings.showDeltaNeutralPriceLines)
            data.push(this.getCurrentPortfolioDeltaNeutralPriceLine(
                marketDataDto?.UnderlyingPrice!,
                portfolioProfile,
                deltaNeutralPriceForCurrentPortfolio,
                portfolioSettings,
                performanceContext,
            ));

        if (deltaNeutralPriceForComparedPortfolio !== null && portfolioSettings.showDeltaNeutralPriceLines)
            data.push(this.getComparedPortfolioDeltaNeutralPriceLine(deltaNeutralPriceForComparedPortfolio, portfolioSettings));

        if (portfolioSettings.showTradeSessionBalanceLine && portfolioSettings.tradeSessionBalance !== 0)
            data.push(this.getTradeSessionBalanceLine(portfolioSettings, performanceContext));
        
        return {
            symbol: 'none',
            lineStyle: {
                width: 0.75,
                color: '#6E7079',
                type: 'dashed'
            },
            data: data,
        };
    }
    
    private getPortfolioMetricAtPrice = (
        portfolioProfile: PortfolioProfile,
        price: number,
        priceGetter: (model: PortfolioRecord) => number
    ): number | null => {
        const records = portfolioProfile.data;

        for (let i = 0; i < records.length - 1; i++) {
            if (records[i].price <= price && records[i + 1].price > price ||
                records[i].price >= price && records[i + 1].price < price)
            {
                return priceGetter(records[i]);
            }
        }

        return null;
    }
    
    private getDeltaNeutralPrice = (portfolioProfile: PortfolioProfile): number | null => {
        const records = portfolioProfile.data;

        for (let i = 0; i < records.length - 1; i++) {
            if (records[i].currentDelta <= 0 && records[i + 1].currentDelta > 0 ||
                records[i].currentDelta >= 0 && records[i + 1].currentDelta < 0)
            {
                return records[i].price;
            }
        }
        
        return null;
    }

    private getZeroBalanceLine = (): any => {
        return {
            name: 'Zero balance line',
            yAxis: 0,
            label: {
                show: false,
            },
            lineStyle: {
                type: 'solid',
                color: this.style.zeroBalanceLineColour,
                width: '1.25'
            }
        }
    }

    private getUnderlyingPriceLine = (
        portfolioProfile: PortfolioProfile,
        marketDataDto: MarketDataDto,
        portfolioSettings: PortfolioSettingsModel,
        deltaNeutralPrice: number | null,
        performanceContext?: PortfolioPerformanceContext,
    ): any => {
        const underlyingPrice = marketDataDto?.UnderlyingPrice!;
        const currentBalanceAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.currentDateBalance);
        const customBalance1AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.customDateBalance1);
        const customBalance2AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.customDateBalance2);
        const customBalance3AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.customDateBalance3);
        const expirationBalanceAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.expirationDateBalance);
        
        const labelContent = currentBalanceAtLine !== null &&
            customBalance1AtLine !== null && customBalance2AtLine !== null && customBalance3AtLine !== null &&
            expirationBalanceAtLine !== null
                ? this.getUnderlyingLabelContent(currentBalanceAtLine, customBalance1AtLine, customBalance2AtLine, customBalance3AtLine,
                    expirationBalanceAtLine, underlyingPrice, portfolioSettings, performanceContext)
                : null
        
        return {
            name: labelContent,
            xAxis: (underlyingPrice - portfolioSettings.minUnderlyingPrice) / portfolioSettings.calculationStep,
            label: {
                show: true,
                position: underlyingPrice > (deltaNeutralPrice ?? 0)
                    ? 'insideEndBottom'
                    : 'insideEndTop',
                formatter: '{b}',
                rich: {
                    currentBalanceStyle: {
                        color: this.style.selectedCurrentLineColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                    customBalanceStyle: {
                        color: this.style.selectedCustomLineColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                },
                fontWeight: this.style.styleFontWeight,
                fontSize: this.style.styleFontSize,
            },
            lineStyle: {
                type: 'solid',
                color: this.style.underlyingLineColour,
                width: '1.25',
            },
        }
    }

    private wrapByStyle<TType>(styleName: string, value: TType): string {
        return `{${styleName}|${value}}`;
    }
    
    private getUnderlyingLabelContent(
        currentBalanceAtLine: number,
        customBalance1AtLine: number,
        customBalance2AtLine: number,
        customBalance3AtLine: number,
        expirationBalanceAtLine: number,
        linePrice: number,
        portfolioSettings: PortfolioSettingsModel,
        performanceContext?: PortfolioPerformanceContext,
    ): string {
        const numberFormatOptions: NumberFormatOptions = {
            useGroupFormat: false,
            alwaysUseSign: true
        };
        
        const result = [];
        const formatBalance = (value: number, alwaysUseSign: boolean = false): string =>
            portfolioSettings.showPortfolioPerformance && performanceContext !== undefined
                ? portfolioPerformanceService.formatMetric(
                    value,
                    performanceContext,
                    portfolioSettings.usePortfolioAnnualisedRoi,
                    alwaysUseSign,
                )
                : alwaysUseSign
                    ? numberFormatters.format0(value, numberFormatOptions)
                    : mathCalculationService.round(value, 0).toString();

        result.push(this.wrapByStyle('currentBalanceStyle', this.style.currentBalanceSuffix));
        result.push(this.wrapByStyle('currentBalanceStyle', formatBalance(currentBalanceAtLine)));

        result.push(`${this.style.groupsDelimiter}`);
        
        result.push(this.wrapByStyle('customBalanceStyle', this.getCustomBalanceSuffix(
            portfolioSettings.isCustomImpliedVolatilityEnabled
        )));
        result.push(this.wrapByStyle('customBalanceStyle', formatBalance(customBalance1AtLine)));

        if (portfolioSettings.isTripleCustomBalanceEnabled) {
            result.push(this.wrapByStyle('customBalanceStyle', this.style.valuesDelimiter));
            result.push(this.wrapByStyle('customBalanceStyle', formatBalance(customBalance2AtLine, true)));
            result.push(this.wrapByStyle('customBalanceStyle', this.style.valuesDelimiter));
            result.push(this.wrapByStyle('customBalanceStyle', formatBalance(customBalance3AtLine, true)));
        }
        
        result.push(`${this.style.newLine}`);

        result.push(this.wrapByStyle('expirationBalanceStyle', this.style.expirationBalanceSuffix));
        result.push(this.wrapByStyle('expirationBalanceStyle', formatBalance(expirationBalanceAtLine)));

        result.push(`${this.style.groupsDelimiter}`);
        
        result.push(`${this.style.priceSuffix}`);
        result.push(`${mathCalculationService.round(linePrice, 0)}`);
        
        return result.join('');
    }
    
    private getPriceLine = (
        underlyingPrice: number,
        linePrice: number,
        showLine: boolean,
        portfolioProfile: PortfolioProfile,
        portfolioSettings: PortfolioSettingsModel,
        performanceContext?: PortfolioPerformanceContext,
    ): any => {
        const currentBalanceAtCurrentPrice = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.currentDateBalance);
        
        const currentBalanceAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.currentDateBalance);
        const customBalance1AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.customDateBalance1);
        const customBalance2AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.customDateBalance2);
        const customBalance3AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.customDateBalance3);
        const expirationBalanceAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.expirationDateBalance);

        const currentDeltaAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.currentDelta);
        const customDeltaAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, linePrice, x => x.customDelta);
        
        const deltaLinePrice = linePrice - underlyingPrice;
        
        const deltaCurrentBalance = currentBalanceAtCurrentPrice !== null && currentBalanceAtLine !== null
            ? currentBalanceAtLine - currentBalanceAtCurrentPrice
            : null;

        const deltaCustomBalance = currentBalanceAtCurrentPrice !== null && customBalance1AtLine !== null
            ? customBalance1AtLine - currentBalanceAtCurrentPrice
            : null;

        const deltaExpirationBalance = currentBalanceAtCurrentPrice !== null && expirationBalanceAtLine !== null
            ? expirationBalanceAtLine - currentBalanceAtCurrentPrice
            : null;
        
        const labelContent = deltaCurrentBalance !== null && currentBalanceAtLine !== null &&
            customBalance1AtLine !== null && deltaCustomBalance !== null &&
            customBalance2AtLine !== null && customBalance3AtLine !== null &&
            expirationBalanceAtLine !== null && deltaExpirationBalance !== null &&
            currentDeltaAtLine !== null && customDeltaAtLine !== null
                ? this.getLineLabelContent(deltaCurrentBalance, deltaCustomBalance, deltaExpirationBalance,
                    currentBalanceAtLine, customBalance1AtLine, customBalance2AtLine, customBalance3AtLine,
                    expirationBalanceAtLine, currentDeltaAtLine, customDeltaAtLine, linePrice, deltaLinePrice,
                    portfolioSettings.showDeltaValues, portfolioSettings.isTripleCustomBalanceEnabled,
                    portfolioSettings.isCustomImpliedVolatilityEnabled,
                    portfolioSettings.showPortfolioPerformance && performanceContext !== undefined,
                    portfolioSettings.usePortfolioAnnualisedRoi,
                    performanceContext)
                : `${linePrice}`
        
        return {
            name: labelContent,
            xAxis: (linePrice - portfolioSettings.minUnderlyingPrice) / portfolioSettings.calculationStep,
            label: {
                show: showLine,
                position: 'end',
                formatter: '{b}',
                rich: {
                    deltaStyle: {
                        color: this.style.deltaColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                    currentBalanceStyle: {
                        color: this.style.selectedCurrentLineColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                    customBalanceStyle: {
                        color: this.style.selectedCustomLineColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                },
                fontWeight: this.style.styleFontWeight,
                fontSize: this.style.styleFontSize,
            },
            lineStyle: {
                color: this.style.trendLineColour,
                width: this.style.trendLineWidth,
                type: this.style.trendLineType,
            }
        }
    }
    
    private getLineLabelContent(
        deltaCurrentBalance: number,
        deltaCustomBalance: number,
        deltaExpirationBalance: number,
        currentBalanceAtLine: number,
        customBalance1AtLine: number,
        customBalance2AtLine: number,
        customBalance3AtLine: number,
        expirationBalanceAtLine: number,
        currentDeltaAtLine: number,
        customDeltaAtLine: number,
        linePrice: number,
        deltaLinePrice: number,
        showDeltaValues: boolean,
        isTripleCustomBalanceEnabled: boolean,
        isCustomImpliedVolatilityEnabled: boolean,
        showPortfolioPerformance: boolean,
        usePortfolioAnnualisedRoi: boolean,
        performanceContext?: PortfolioPerformanceContext,
    ): string {
        const result = [];

        const numberFormatOptions: NumberFormatOptions = {
            useGroupFormat: false,
            alwaysUseSign: true
        };
        const formatBalance = (value: number): string =>
            showPortfolioPerformance && performanceContext !== undefined
                ? portfolioPerformanceService.formatMetric(
                    value,
                    performanceContext,
                    usePortfolioAnnualisedRoi,
                    true,
                )
                : numberFormatters.format0(value, numberFormatOptions);
        
        result.push(this.wrapByStyle('currentBalanceStyle', this.style.currentBalanceSuffix));
        result.push(this.wrapByStyle('currentBalanceStyle', formatBalance(currentBalanceAtLine)));

        if (showDeltaValues) {
            result.push(this.wrapByStyle('currentBalanceStyle', ' ('));
            result.push(this.wrapByStyle('currentBalanceStyle', formatBalance(deltaCurrentBalance)));
            result.push(this.wrapByStyle('currentBalanceStyle', ')'));
        }

        result.push(`${this.style.groupsDelimiter}`);
        
        result.push(this.wrapByStyle('currentBalanceStyle', this.style.deltaSuffix));
        result.push(this.wrapByStyle('currentBalanceStyle', numberFormatters.format3(currentDeltaAtLine, numberFormatOptions)));

        result.push(`${this.style.newLine}`);
        
        result.push(this.wrapByStyle('customBalanceStyle', this.getCustomBalanceSuffix(isCustomImpliedVolatilityEnabled)));
        result.push(this.wrapByStyle('customBalanceStyle', formatBalance(customBalance1AtLine)));
        
        if (isTripleCustomBalanceEnabled) {
            result.push(this.wrapByStyle('customBalanceStyle', this.style.valuesDelimiter));
            result.push(this.wrapByStyle('customBalanceStyle', formatBalance(customBalance2AtLine)));
            result.push(this.wrapByStyle('customBalanceStyle', this.style.valuesDelimiter));
            result.push(this.wrapByStyle('customBalanceStyle', formatBalance(customBalance3AtLine)));
        }
        
        if (showDeltaValues) {
            result.push(this.wrapByStyle('customBalanceStyle', ' ('));
            result.push(this.wrapByStyle('customBalanceStyle', formatBalance(deltaCustomBalance)));
            result.push(this.wrapByStyle('customBalanceStyle', ')'));
        }

        result.push(`${this.style.groupsDelimiter}`);

        result.push(this.wrapByStyle('customBalanceStyle', this.style.deltaSuffix));
        result.push(this.wrapByStyle('customBalanceStyle', numberFormatters.format3(customDeltaAtLine, numberFormatOptions)));

        result.push(`${this.style.newLine}`);

        result.push(this.wrapByStyle('expirationBalanceStyle', this.style.expirationBalanceSuffix));
        result.push(this.wrapByStyle('expirationBalanceStyle', formatBalance(expirationBalanceAtLine)));

        if (showDeltaValues) {
            result.push(this.wrapByStyle('expirationBalanceStyle', ' ('));
            result.push(this.wrapByStyle('expirationBalanceStyle', formatBalance(deltaExpirationBalance)));
            result.push(this.wrapByStyle('expirationBalanceStyle', ')'));
        }
        
        result.push(`${this.style.groupsDelimiter}`);

        result.push(`${this.style.priceSuffix}`);
        result.push(`${linePrice}`);

        if (showDeltaValues) {
            result.push(' (');
            result.push(this.wrapByStyle('deltaStyle', numberFormatters.format0(deltaLinePrice, numberFormatOptions)));
            result.push(')');
        }
        
        return result.join('');
    }
    
    private getCurrentPortfolioDeltaNeutralPriceLine = (
        underlyingPrice: number,
        portfolioProfile: PortfolioProfile,
        deltaNeutralPrice: number,
        portfolioSettings: PortfolioSettingsModel,
        performanceContext?: PortfolioPerformanceContext,
    ): any => {
        const currentBalanceAtCurrentPrice = this.getPortfolioMetricAtPrice(portfolioProfile, underlyingPrice, x => x.currentDateBalance);
        const currentBalanceAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.currentDateBalance);
        const customBalance1AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.customDateBalance1);
        const customBalance2AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.customDateBalance2);
        const customBalance3AtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.customDateBalance3);
        const expirationBalanceAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.expirationDateBalance);

        const currentDeltaAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.currentDelta) ?? 0;
        const customDeltaAtLine = this.getPortfolioMetricAtPrice(portfolioProfile, deltaNeutralPrice, x => x.customDelta) ?? 0;
        
        const deltaCurrentBalance = currentBalanceAtCurrentPrice !== null && currentBalanceAtLine !== null
            ? currentBalanceAtLine - currentBalanceAtCurrentPrice
            : null;

        const deltaCustomBalance = currentBalanceAtCurrentPrice !== null && customBalance1AtLine !== null
            ? customBalance1AtLine - currentBalanceAtCurrentPrice
            : null;

        const deltaExpirationBalance = currentBalanceAtCurrentPrice !== null && expirationBalanceAtLine !== null
            ? expirationBalanceAtLine - currentBalanceAtCurrentPrice
            : null;
        
        const deltaCurrentPrice = mathCalculationService.round(deltaNeutralPrice - underlyingPrice, 0);
        
        const labelContent = deltaCurrentBalance !== null && currentBalanceAtLine !== null &&
            customBalance1AtLine !== null && deltaCustomBalance !== null &&
            customBalance2AtLine !== null && customBalance3AtLine !== null &&
            expirationBalanceAtLine !== null && deltaExpirationBalance !== null
                ? this.getLineLabelContent(deltaCurrentBalance, deltaCustomBalance, deltaExpirationBalance, currentBalanceAtLine,
                    customBalance1AtLine, customBalance2AtLine, customBalance3AtLine, expirationBalanceAtLine,
                    currentDeltaAtLine, customDeltaAtLine, deltaNeutralPrice, deltaCurrentPrice, portfolioSettings.showDeltaValues,
                    portfolioSettings.isTripleCustomBalanceEnabled, portfolioSettings.isCustomImpliedVolatilityEnabled,
                    portfolioSettings.showPortfolioPerformance && performanceContext !== undefined,
                    portfolioSettings.usePortfolioAnnualisedRoi,
                    performanceContext)
                : null
        
        return {
            name: labelContent,
            xAxis: (deltaNeutralPrice - portfolioSettings.minUnderlyingPrice) / portfolioSettings.calculationStep,
            label: {
                show: true,
                position: underlyingPrice < deltaNeutralPrice
                    ? 'insideEndBottom'
                    : 'insideEndTop',
                formatter: '{b}',
                rich: {
                    deltaStyle: {
                        color: this.style.deltaColour,
                        fontWeight: 'semibold',
                        fontSize: '12',
                    },
                    currentBalanceStyle: {
                        color: this.style.selectedCurrentLineColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                    customBalanceStyle: {
                        color: this.style.selectedCustomLineColour,
                        fontWeight: this.style.styleFontWeight,
                        fontSize: this.style.styleFontSize,
                    },
                },
                fontWeight: this.style.styleFontWeight,
                fontSize: this.style.styleFontSize,
            },
            lineStyle: {
                width: 0.75,
            }
        }
    }
    
    private getComparedPortfolioDeltaNeutralPriceLine = (
        deltaNeutralPriceForComparedPortfolio: number,
        portfolioSettings: PortfolioSettingsModel
    ): any => {
        return {
            xAxis: (deltaNeutralPriceForComparedPortfolio - portfolioSettings.minUnderlyingPrice) / portfolioSettings.calculationStep,
            label: {
                show: false,
            },
            lineStyle: {
                color: this.style.comparedCurrentLineColour,
            }
        }
    }

    private getTradeSessionBalanceLine = (
        portfolioSettings: PortfolioSettingsModel,
        performanceContext?: PortfolioPerformanceContext,
    ): any => {
        const sessionBalance = portfolioSettings.tradeSessionBalance;
        const balanceToDisplay = portfolioSettings.showPortfolioPerformance && performanceContext !== undefined
            ? portfolioPerformanceService.formatMetric(
                sessionBalance,
                performanceContext,
                portfolioSettings.usePortfolioAnnualisedRoi,
            )
            : sessionBalance;
        
        return {
            name: `${this.style.currentBalanceSuffix}${balanceToDisplay}`,
            yAxis: portfolioSettings.tradeSessionBalance,
            label: {
                show: portfolioSettings.showTradeSessionBalanceLine && sessionBalance !== 0,
                position: sessionBalance > 0 ? 'insideStartTop' : 'insideStartBottom',
                formatter: '{b}',
                fontWeight: this.style.styleFontWeight,
                fontSize: this.style.styleFontSize,
                offset: [5, 0],
                color: this.style.tradeSessionBalanceLineColour,
            },
            lineStyle: {
                color: this.style.tradeSessionBalanceLineColour,
            }
        }
    }

    private getCustomBalanceSuffix = (isCustomImpliedVolatilityEnabled: boolean): string =>
        isCustomImpliedVolatilityEnabled
            ? this.style.customImpliedVolatilityAbdBalanceSuffix
            : this.style.customBalanceSuffix;
}
